import { useState } from 'react'
import { Search, RefreshCw, Download, AlertTriangle, CheckCircle2, Info, XCircle, ChevronLeft, ChevronRight } from 'lucide-react'

const LOG_META = {
  info:    { icon: Info,          cls:'badge-info',    label:'INFO',    color:'#06B6D4', colorGlow:'rgba(6,182,212,0.15)',   variant:'cyan',  trendColor:'#06B6D4' },
  success: { icon: CheckCircle2,  cls:'badge-active',  label:'SUCCESS', color:'#10B981', colorGlow:'rgba(16,185,129,0.15)',  variant:'green', trendColor:'#10B981' },
  warning: { icon: AlertTriangle, cls:'badge-warning', label:'WARN',    color:'#F59E0B', colorGlow:'rgba(245,158,11,0.15)',  variant:'amber', trendColor:'#F59E0B' },
  error:   { icon: XCircle,       cls:'badge-danger',  label:'ERROR',   color:'#EF4444', colorGlow:'rgba(239,68,68,0.15)',   variant:'red',   trendColor:'#EF4444' },
}

const LOGS = [
  { id:'SYS-5091', type:'error',   service:'DATABASE',   message:'Connection pool exhausted — max 100 connections reached on db-primary',          time:'15:47:22' },
  { id:'SYS-5090', type:'success', service:'DEPLOY',     message:'Hotfix v2.14.3 deployed to production cluster — all 6 nodes healthy',            time:'15:41:05' },
  { id:'SYS-5089', type:'warning', service:'MEMORY',     message:'Heap usage at 87% on app-server-02 — GC pressure increasing',                   time:'15:38:50' },
  { id:'SYS-5088', type:'info',    service:'SCHEDULER',  message:'Nightly cron job triggered: subscription renewal batch (4,200 records)',         time:'15:30:00' },
  { id:'SYS-5087', type:'error',   service:'STORAGE',    message:'S3 bucket write failure: media-uploads — AccessDenied (IAM policy mismatch)',    time:'15:22:14' },
  { id:'SYS-5086', type:'success', service:'BACKUP',     message:'Full DB snapshot completed and uploaded to cold storage (18.7 GB in 4m 12s)',    time:'15:00:00' },
  { id:'SYS-5085', type:'warning', service:'QUEUE',      message:'RabbitMQ consumer lag: email-outbound queue backed up to 3,840 messages',        time:'14:54:33' },
  { id:'SYS-5084', type:'info',    service:'AUTH',       message:'OAuth2 token rotated for third-party integration: DataSync Pro',                 time:'14:48:17' },
  { id:'SYS-5083', type:'success', service:'SSL',        message:'TLS certificate auto-renewed for api.platform.io — valid 90 days',              time:'14:40:00' },
  { id:'SYS-5082', type:'error',   service:'PAYMENT',    message:'Stripe webhook delivery failed (3 retries) — endpoint returned 503',            time:'14:31:09' },
  { id:'SYS-5081', type:'warning', service:'CPU',        message:'Node cpu-03 sustained 93% utilisation for 2 min — auto-scaling triggered',      time:'14:22:41' },
  { id:'SYS-5080', type:'info',    service:'CDN',        message:'Cache purge completed for /static/assets — 12,400 objects invalidated',         time:'14:15:00' },
  { id:'SYS-5079', type:'success', service:'MIGRATION',  message:'Schema migration #0047 applied — added composite index on transactions table',   time:'14:08:55' },
  { id:'SYS-5078', type:'error',   service:'SEARCH',     message:'Elasticsearch cluster red: shard allocation failed on index logs-2025-06',      time:'13:58:30' },
  { id:'SYS-5077', type:'warning', service:'RATE-LIMIT', message:'API rate limit breached: workspace #8812 exceeded 5,000 req/min threshold',     time:'13:49:12' },
  { id:'SYS-5076', type:'info',    service:'AUDIT',      message:'Role permission change logged — "reports_export" granted to role: Analyst',      time:'13:40:00' },
  { id:'SYS-5075', type:'success', service:'HEALTH',     message:'Liveness probe passed on all 8 microservices — avg latency 42ms',               time:'13:30:00' },
  { id:'SYS-5074', type:'warning', service:'DISK',       message:'Disk usage at 79% on /var/log — log rotation recommended within 48h',           time:'13:18:04' },
  { id:'SYS-5073', type:'error',   service:'SMTP',       message:'Email relay rejected: SPF hard fail for domain outbound.company.io',             time:'13:05:47' },
  { id:'SYS-5072', type:'info',    service:'CONFIG',     message:'Environment variable FEATURE_AI_INSIGHTS toggled ON via remote config',         time:'12:55:00' },
  { id:'SYS-5071', type:'success', service:'ANALYTICS',  message:'Daily aggregation pipeline completed — 2.1M events processed in 38s',           time:'12:00:00' },
  { id:'SYS-5070', type:'warning', service:'FIREWALL',   message:'Geo-block rule triggered: 48 requests from restricted region blocked',          time:'11:50:19' },
  { id:'SYS-5069', type:'info',    service:'SESSION',    message:'Idle session cleanup: 340 expired tokens purged from Redis',                    time:'11:30:00' },
  { id:'SYS-5068', type:'error',   service:'WEBHOOK',    message:'Outbound webhook timeout (>10s) to partner endpoint: crm.partner.net',          time:'11:17:33' },
  { id:'SYS-5067', type:'success', service:'CACHE',      message:'Redis cluster rebalanced after node addition — memory usage normalised at 54%',  time:'11:00:00' },
  { id:'SYS-5066', type:'warning', service:'LICENSE',    message:'Enterprise license utilisation at 92% — 44 seats of 480 remaining',             time:'10:45:08' },
  { id:'SYS-5065', type:'info',    service:'MONITOR',    message:'Uptime check registered: new endpoint /api/v3/health added to watchlist',       time:'10:30:00' },
  { id:'SYS-5064', type:'error',   service:'NETWORK',    message:'Packet loss 8% detected on VPC peering link between us-east-1 and eu-west-2',   time:'10:12:55' },
  { id:'SYS-5063', type:'success', service:'SEED',       message:'Staging environment re-seeded with anonymised production snapshot (v6)',         time:'09:55:00' },
  { id:'SYS-5062', type:'info',    service:'CRON',       message:'Weekly report generation job queued — estimated completion: 4 min',             time:'09:00:00' },
]

/* ─── Reusable Pagination ────────────────────────────────────── */
function Pagination({ page, totalPages, total, perPage, onPageChange, onPerPageChange }) {
  const from = total === 0 ? 0 : (page - 1) * perPage + 1
  const to   = Math.min(page * perPage, total)

  const pages = []
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
  } else {
    pages.push(1)
    if (page > 3) pages.push('...')
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i)
    if (page < totalPages - 2) pages.push('...')
    pages.push(totalPages)
  }

  const btnBase = {
    minWidth: 32, height: 32, padding: '0 6px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    background: 'transparent', cursor: 'pointer', fontSize: 'var(--fs-xs)',
    fontFamily: 'var(--font-body)', color: 'var(--text2)',
    transition: 'all 150ms ease',
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px var(--space-lg)', borderTop: '1px solid var(--border)',
      flexWrap: 'wrap', gap: 8,
    }}>
      {/* Left: info + per-page */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
        <span>Showing <b style={{ color: 'var(--text2)' }}>{from}–{to}</b> of <b style={{ color: 'var(--text2)' }}>{total}</b></span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Rows:</span>
          <select
            value={perPage}
            onChange={e => onPerPageChange(Number(e.target.value))}
            style={{
              background: 'var(--bg-raised)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', color: 'var(--text2)',
              fontSize: 'var(--fs-xs)', fontFamily: 'var(--font-body)',
              padding: '2px 20px 2px 6px', cursor: 'pointer', outline: 'none',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 5px center',
            }}
          >
            {[5, 10, 15, 20].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Right: page buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          style={{ ...btnBase, opacity: page === 1 ? 0.4 : 1, cursor: page === 1 ? 'not-allowed' : 'pointer' }}
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
        >
          <ChevronLeft size={14} />
        </button>

        {pages.map((p, i) =>
          p === '...'
            ? <span key={`dots-${i}`} style={{ padding: '0 4px', color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>…</span>
            : <button
                key={p}
                onClick={() => onPageChange(p)}
                style={{
                  ...btnBase,
                  background: page === p ? 'var(--accent)' : 'transparent',
                  borderColor: page === p ? 'var(--accent)' : 'var(--border)',
                  color: page === p ? '#fff' : 'var(--text2)',
                  fontWeight: page === p ? 600 : 400,
                }}
              >{p}</button>
        )}

        <button
          style={{ ...btnBase, opacity: page === totalPages ? 0.4 : 1, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  )
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function SystemLogs() {
  const [filter,  setFilter]  = useState('all')
  const [search,  setSearch]  = useState('')
  const [page,    setPage]    = useState(1)
  const [perPage, setPerPage] = useState(10)

  const visible = LOGS.filter(l => {
    const matchType   = filter === 'all' || l.type === filter
    const matchSearch = !search ||
      l.message.toLowerCase().includes(search.toLowerCase()) ||
      l.service.toLowerCase().includes(search.toLowerCase()) ||
      l.id.toLowerCase().includes(search.toLowerCase())
    return matchType && matchSearch
  })

  const totalPages = Math.max(1, Math.ceil(visible.length / perPage))
  const safePage   = Math.min(page, totalPages)
  const paginated  = visible.slice((safePage - 1) * perPage, safePage * perPage)

  const counts = LOGS.reduce((a, l) => ({ ...a, [l.type]: (a[l.type] || 0) + 1 }), {})

  function handleFilter(f) {
    setFilter(prev => prev === f ? 'all' : f)
    setPage(1)
  }

  function handleSearch(val) {
    setSearch(val)
    setPage(1)
  }

  function handlePerPage(n) {
    setPerPage(n)
    setPage(1)
  }

  return (
    <div className="dash-page page-in">

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'var(--space-md)' }}>
        <h1 className="page-title">System Logs</h1>
        <div style={{ display:'flex', gap:'var(--space-sm)' }}>
          <button className="btn btn-outline"><RefreshCw size={14}/> Refresh</button>
          <button className="btn btn-primary"><Download size={14}/> Export</button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="stats-grid cols-4">
        {Object.entries(LOG_META).map(([key, meta]) => {
          const IconComp = meta.icon
          const count    = counts[key] || 0
          const isActive = filter === key
          return (
            <div
              key={key}
              className={`stat-card stat-card--${meta.variant}`}
              style={{ cursor:'pointer', outline: isActive ? `2px solid ${meta.color}` : 'none', outlineOffset:2 }}
              onClick={() => handleFilter(key)}
            >
              <span style={{
                position:'absolute', top:0, right:0, width:110, height:110,
                borderRadius:'50%', background:meta.colorGlow,
                filter:'blur(30px)', transform:'translate(30%,-30%)', pointerEvents:'none',
              }}/>
              <div className="stat-card-header">
                <div className="stat-card-icon" style={{ background:meta.colorGlow, color:meta.color, border:`1px solid ${meta.colorGlow}` }}>
                  <IconComp size={15} strokeWidth={1.8}/>
                </div>
                <span className={`badge ${meta.cls}`}>{meta.label}</span>
              </div>
              <div className="stat-card-value">{count}</div>
              <div className="stat-card-label">Events today</div>
              <div style={{
                position:'absolute', bottom:0, left:0, right:0, height:2,
                background:`linear-gradient(90deg,${meta.color}88,transparent)`,
                borderRadius:'0 0 var(--radius-lg) var(--radius-lg)',
              }}/>
            </div>
          )
        })}
      </div>

      {/* Table Card */}
      <div className="card" style={{ padding:0 }}>

        {/* Toolbar */}
        <div style={{
          display:'flex', gap:'var(--space-sm)',
          padding:'var(--space-md) var(--space-lg)',
          borderBottom:'1px solid var(--border)',
          flexWrap:'wrap', alignItems:'center',
        }}>
          <div style={{ position:'relative', flex:1, minWidth:180 }}>
            <Search size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text3)' }}/>
            <input
              className="input"
              style={{ paddingLeft:32 }}
              placeholder="Search by log ID, service, or message…"
              value={search}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
          <div className="view-tabs" style={{ margin:0 }}>
            {['all','info','success','warning','error'].map(f => (
              <button key={f} className={`vtab${filter === f ? ' active' : ''}`} onClick={() => handleFilter(f)}>
                {f === 'all' ? 'All' : LOG_META[f].label}
              </button>
            ))}
          </div>
          <span style={{ fontSize:'var(--fs-xs)', color:'var(--text3)', whiteSpace:'nowrap' }}>
            {visible.length} / {LOGS.length} logs
          </span>
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width:110 }}>Log ID</th>
                <th style={{ width:100 }}>Level</th>
                <th style={{ width:130 }}>Service</th>
                <th>Message</th>
                <th style={{ width:130 }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign:'center', color:'var(--text3)', padding:'2.5rem' }}>
                    No logs match your filter.
                  </td>
                </tr>
              ) : paginated.map(log => {
                const m = LOG_META[log.type]
                const IconComp = m.icon
                return (
                  <tr key={log.id}>
                    <td style={{ fontFamily:'var(--font-mono)', fontSize:'var(--fs-xs)', color:'var(--accent-hover)' }}>
                      {log.id}
                    </td>
                    <td>
                      <span className={`badge ${m.cls}`} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                        <IconComp size={11}/>
                        {m.label}
                      </span>
                    </td>
                    <td>
                      <span style={{
                        fontFamily:'var(--font-mono)', fontSize:'var(--fs-xs)',
                        color:m.color, background:`${m.color}12`,
                        padding:'2px 8px', borderRadius:4,
                      }}>
                        {log.service}
                      </span>
                    </td>
                    <td style={{ maxWidth:420, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {log.message}
                    </td>
                    <td style={{ fontFamily:'var(--font-mono)', fontSize:'var(--fs-xs)', color:'var(--text3)', whiteSpace:'nowrap' }}>
                      Today · {log.time}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          page={safePage}
          totalPages={totalPages}
          total={visible.length}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={handlePerPage}
        />
      </div>

      <style>{`.dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}`}</style>
    </div>
  )
}